import React, { useState, useEffect } from 'react';
import { AlertTriangle, Server, Clock, Ban, Lock, Wifi, HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { UpdateResult } from '../types';
import './ErrorReport.css';

interface ErrorReportProps {
  updateResult: UpdateResult | null;
  isVisible: boolean;
  onToggle: () => void;
}

interface ErrorDetail {
  clientName: string;
  clientIP: string;
  error: string;
  errorType: string;
}

const ErrorReport: React.FC<ErrorReportProps> = ({ updateResult, isVisible, onToggle }) => {
  const [groupedErrors, setGroupedErrors] = useState<{ [key: string]: ErrorDetail[] }>({});

  useEffect(() => {
    if (updateResult?.errorDetails) {
      const grouped = updateResult.errorDetails.reduce((acc: { [key: string]: ErrorDetail[] }, error) => {
        const type = error.errorType || 'ERROR_DESCONOCIDO';
        if (!acc[type]) acc[type] = [];
        acc[type].push(error);
        return acc;
      }, {});
      setGroupedErrors(grouped);
    }
  }, [updateResult]);

  const getErrorIcon = (errorType: string) => {
    switch (errorType) {
      case 'SERVIDOR_OFFLINE':
        return <Server size={16} />;
      case 'TIMEOUT':
        return <Clock size={16} />;
      case 'API_NO_ENCONTRADA':
        return <Ban size={16} />;
      case 'ACCESO_DENEGADO':
        return <Lock size={16} />;
      case 'ERROR_SSL':
        return <Lock size={16} />;
      case 'DNS_ERROR':
        return <Wifi size={16} />;
      default:
        return <HelpCircle size={16} />;
    }
  };

  const getErrorDescription = (errorType: string) => {
    switch (errorType) {
      case 'SERVIDOR_OFFLINE':
        return 'El servidor no está respondiendo o está apagado';
      case 'TIMEOUT':
        return 'El servidor tardó demasiado en responder';
      case 'API_NO_ENCONTRADA':
        return 'La API no existe en este servidor (404)';
      case 'ACCESO_DENEGADO':
        return 'Acceso denegado por el servidor (403)';
      case 'NO_AUTORIZADO':
        return 'Token de autorización inválido (401)';
      case 'ERROR_SSL':
        return 'Problema con certificados SSL/HTTPS';
      case 'DNS_ERROR':
        return 'No se puede resolver la dirección del servidor';
      default:
        return 'Error no identificado';
    }
  };

  const getErrorColor = (errorType: string) => {
    switch (errorType) {
      case 'SERVIDOR_OFFLINE':
        return '#EF4444'; // Rojo
      case 'TIMEOUT':
        return '#F59E0B'; // Amarillo
      case 'API_NO_ENCONTRADA':
        return '#8B5CF6'; // Púrpura
      case 'ACCESO_DENEGADO':
        return '#F97316'; // Naranja
      case 'NO_AUTORIZADO':
        return '#DC2626'; // Rojo oscuro
      case 'ERROR_SSL':
        return '#059669'; // Verde
      case 'DNS_ERROR':
        return '#3B82F6'; // Azul
      default:
        return '#6B7280'; // Gris
    }
  };

  if (!updateResult || updateResult.errorCount === 0) {
    return null;
  }

  return (
    <div className="error-report">
      <div className="error-report-header" onClick={onToggle}>
        <div className="error-report-title">
          <AlertTriangle size={20} />
          <h3>Reporte de Errores ({updateResult.errorCount})</h3>
        </div>
        {isVisible ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </div>

      {isVisible && (
        <div className="error-report-content">
          <div className="error-summary">
            <p>
              {updateResult.successCount} clientes actualizados correctamente, 
              {updateResult.errorCount} con errores
            </p>
          </div>

          <div className="error-groups">
            {Object.entries(groupedErrors).map(([errorType, errors]) => (
              <div key={errorType} className="error-group">
                <div 
                  className="error-group-header"
                  style={{ borderLeftColor: getErrorColor(errorType) }}
                >
                  <div className="error-group-info">
                    <div className="error-group-title">
                      {getErrorIcon(errorType)}
                      <span>{errorType.replace(/_/g, ' ')}</span>
                      <span className="error-count">({errors.length})</span>
                    </div>
                    <p className="error-description">
                      {getErrorDescription(errorType)}
                    </p>
                  </div>
                </div>

                <div className="error-clients">
                  {errors.map((error, index) => (
                    <div key={index} className="error-client">
                      <div className="client-info">
                        <span className="client-name">{error.clientName}</span>
                        <span className="client-ip">{error.clientIP}</span>
                      </div>
                      <div className="error-message">
                        {String(error.error).length > 100 
                          ? String(error.error).substring(0, 100) + '...'
                          : String(error.error)
                        }
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="error-suggestions">
            <h4>💡 Sugerencias para solucionar errores:</h4>
            <ul>
              <li><strong>SERVIDOR_OFFLINE:</strong> Verificar que el servidor esté encendido y conectado</li>
              <li><strong>ACCESO_DENEGADO:</strong> Verificar que la API esté instalada y configurada</li>
              <li><strong>API_NO_ENCONTRADA:</strong> Instalar la API en el path correcto</li>
              <li><strong>TIMEOUT:</strong> Revisar conectividad de red del servidor</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default ErrorReport;
